import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Select, Spin } from 'antd'
import { SelectProps } from 'antd/lib'

import {
  ClassifierServiceGetInvestigationTestTypeEndpointDefaultResponse,
  useClassifierServiceGetListInvestigationTestTypeEndpointKey
} from 'src/api/queries'
import {
  ClassifierService,
  GetCommonClassifierElementResponse
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useLocales } from 'src/locales'

type Props = {
  investigationTypeIds?: string[]
} & SelectProps

export const TestTypesInfiniteSelect = ({
  investigationTypeIds,
  ...props
}: Props) => {
  const { currentLang, t } = useLocales()

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    // Keyed by the investigation types too: selects filtered by different
    // types (e.g. one per screening test row) must not share a cache entry.
    queryKey: [
      useClassifierServiceGetListInvestigationTestTypeEndpointKey,
      investigationTypeIds
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getListInvestigationTestTypeEndpoint(
          pageParam,
          DEFAULT_PER_PAGE,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          investigationTypeIds
        )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetCommonClassifierElementResponse[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const selectOptions = data?.pages.flatMap(
    (page) =>
      page.data?.map(
        (
          item: ClassifierServiceGetInvestigationTestTypeEndpointDefaultResponse
        ) => ({
          value: item?.id,
          label: item?.title?.[currentLang?.value]
        })
      ) || []
  )

  return (
    <Select
      allowClear
      filterOption={false}
      options={selectOptions}
      loading={isFetching}
      showSearch={false}
      placeholder={t('common.select_from_list')}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
